// The session's Ledger (F-SES-21 rule 6, spec/13 §3.3): its journal entries, newest first, ten rows at a time.
// Runs of identical charges fold into one row that opens to its entries (`@clavian/domain/ledger-runs`); each row says when it
// was posted, which source paid it (pass, free time, wallet or pay-as-you-go), the play time it used and the money
// it moved, ₹0.00 when a pass or free time covered it. Older rows come a page at a time from the api's cursor.

import type { SessionLedgerEntry } from '@clavian/api-client';
import { ledgerRuns, ledgerRunTotals, type LedgerRun } from '@clavian/domain/ledger-runs';
import { useTranslation } from '@clavian/i18n/react';
import { Button, Skeleton } from '@clavian/ui-web';
import { useMemo, useState, type ReactNode } from 'react';

import { Icon } from '../../shell/nav-icons';
import { formatDuration, formatMoney } from './money';
import { useSessionLedger } from './queries';

/** How many rows the list shows at first, and how many more each "Show older" adds. */
export const LEDGER_ROWS_STEP = 10;

type ConsoleT = ReturnType<typeof useTranslation>['t'];

export function SessionLedger({ organizationId, sessionId }: { readonly organizationId: string; readonly sessionId: string }): ReactNode {
  const { t } = useTranslation('console');
  const ledger = useSessionLedger(organizationId, sessionId);
  const [shown, setShown] = useState(LEDGER_ROWS_STEP);
  // Open runs, by the ids they hold: a run that grows (a newer charge, an older page) stays open.
  const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set());

  const entries = useMemo(() => ledger.data?.pages.flatMap((page) => page.data) ?? [], [ledger.data]);
  const rows = useMemo(() => ledgerRuns(entries), [entries]);

  if (ledger.isPending) {
    return (
      <>
        <LedgerHeading t={t} />
        <Skeleton shape="text" lines={3} />
      </>
    );
  }
  if (ledger.isError) {
    return (
      <>
        <LedgerHeading t={t} />
        <p className="console-muted">
          {t('sessions.timeline.ledgerError')}{' '}
          <Button type="button" variant="ghost" size="sm" onClick={() => void ledger.refetch()}>
            {t('sessions.timeline.ledgerRetry')}
          </Button>
        </p>
      </>
    );
  }
  if (rows.length === 0) return null;

  const visible = rows.slice(0, shown);
  const hasOlder = rows.length > shown || ledger.hasNextPage;
  // Only when older rows are on screen past the first ten: nothing to go back from otherwise.
  const canGoBack = shown > LEDGER_ROWS_STEP && rows.length > LEDGER_ROWS_STEP;
  const showOlder = (): void => {
    const next = shown + LEDGER_ROWS_STEP;
    setShown(next);
    // The last loaded run may go on into the next page, so read it before the rows run out, not after.
    if (rows.length <= next && ledger.hasNextPage && !ledger.isFetchingNextPage) void ledger.fetchNextPage();
  };
  const isOpen = (row: LedgerRun<SessionLedgerEntry>): boolean => row.entries.some((entry) => open.has(entry.id));
  const toggle = (row: LedgerRun<SessionLedgerEntry>): void => {
    const wasOpen = isOpen(row);
    setOpen((current) => {
      const next = new Set(current);
      for (const entry of row.entries) {
        if (wasOpen) next.delete(entry.id);
        else next.add(entry.id);
      }
      return next;
    });
  };

  return (
    <>
      <LedgerHeading t={t} />
      <ul className="console-session__ledger" aria-label={t('sessions.timeline.ledgerTitle')}>
        {visible.map((row) =>
          row.entries.length === 1 ? (
            <EntryRow key={row.key} entry={row.entries[0]!} t={t} />
          ) : (
            <RunRow key={row.key} row={row} open={isOpen(row)} onToggle={() => toggle(row)} t={t} />
          ),
        )}
      </ul>
      {hasOlder || canGoBack ? (
        <div className="console-session__ledger-more">
          {hasOlder ? (
            <Button type="button" variant="secondary" size="sm" onClick={showOlder} disabled={ledger.isFetchingNextPage}>
              {ledger.isFetchingNextPage ? t('sessions.timeline.ledgerLoadingOlder') : t('sessions.timeline.ledgerShowOlder')}
            </Button>
          ) : null}
          {canGoBack ? (
            <Button type="button" variant="ghost" size="sm" onClick={() => setShown(LEDGER_ROWS_STEP)}>
              {t('sessions.timeline.ledgerShowLatest')}
            </Button>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

function LedgerHeading({ t }: { readonly t: ConsoleT }): ReactNode {
  return (
    <h4 className="console-session__block-subtitle">
      {t('sessions.timeline.ledgerTitle')}
      <span className="console-subtle"> · {t('sessions.timeline.ledgerNewestFirst')}</span>
    </h4>
  );
}

/** "Charge · Pass", "Free time added · Free time", "Charge reversed · Pay-as-you-go · reversed". */
function entryLabel(entry: SessionLedgerEntry, t: ConsoleT): string {
  const parts = [t(`sessions.timeline.entry.${entry.kind}`, { defaultValue: entry.memo ?? entry.kind })];
  if (entry.funding !== undefined) parts.push(t(`sessions.source.${entry.funding}`, { defaultValue: entry.funding }));
  return parts.join(' · ');
}

/** A signed duration: a reversal gives play time back, "−0:04". Blank when the entry used none. */
function signedDuration(seconds: number): string {
  if (seconds === 0) return '';
  return seconds < 0 ? `−${formatDuration(-seconds)}` : formatDuration(seconds);
}

/** One journal entry: what it was and who paid, when, the play time it used and the money it moved. */
function EntryRow({ entry, t, nested = false }: { readonly entry: SessionLedgerEntry; readonly t: ConsoleT; readonly nested?: boolean }): ReactNode {
  const className = [
    'console-session__ledger-row',
    entry.reversal ? 'console-session__ledger-row--reversal' : '',
    nested ? 'console-session__ledger-row--nested' : '',
  ]
    .filter((name) => name !== '')
    .join(' ');
  return (
    <li className={className}>
      <span className="console-session__ledger-label">
        {entryLabel(entry, t)}
        {entry.reversal ? <span className="console-subtle"> · {t('sessions.timeline.reversal')}</span> : null}
      </span>
      <time className="console-session__ledger-figure console-session__ledger-time clav-figure" dateTime={entry.at}>
        {formatClock(entry.at)}
      </time>
      <span className="console-session__ledger-figure clav-figure">{signedDuration(entry.seconds)}</span>
      <span className="console-session__ledger-figure clav-figure">{formatMoney(entry.amount)}</span>
    </li>
  );
}

/** A run of identical charges: "Charge · Pass · 0:01 each × 27", its span and totals; opens to its entries. */
function RunRow({ row, open, onToggle, t }: { readonly row: LedgerRun<SessionLedgerEntry>; readonly open: boolean; readonly onToggle: () => void; readonly t: ConsoleT }): ReactNode {
  const newest = row.entries[0]!;
  const oldest = row.entries.at(-1)!;
  const totals = ledgerRunTotals(row);
  const each = newest.seconds !== 0 ? signedDuration(newest.seconds) : formatMoney(newest.amount);
  const listId = `ledger-run-${oldest.id}`;
  const label = entryLabel(newest, t);
  const eachText = t('sessions.timeline.ledgerEach', { each, count: row.entries.length });
  return (
    <li className="console-session__ledger-row">
      <span className="console-session__ledger-label">
        {/* Named in full: the visible text, whose spacing an accessible-name walk would otherwise trim. */}
        <button
          type="button"
          className="console-session__ledger-toggle"
          aria-label={`${label} · ${eachText}`}
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          onClick={onToggle}
        >
          <span className={open ? 'console-session__ledger-chevron is-open' : 'console-session__ledger-chevron'} aria-hidden="true">
            <Icon name="chevronRight" />
          </span>
          {label}
          <span className="console-subtle"> · {eachText}</span>
        </button>
      </span>
      <span className="console-session__ledger-figure console-session__ledger-time clav-figure">
        <time dateTime={oldest.at}>{formatClock(oldest.at)}</time>
        {t('sessions.timeline.ledgerRangeTo')}
        <time dateTime={newest.at}>{formatClock(newest.at)}</time>
      </span>
      <span className="console-session__ledger-figure clav-figure">{signedDuration(totals.seconds)}</span>
      <span className="console-session__ledger-figure clav-figure">{formatMoney(totals.amount)}</span>
      {open ? (
        <ul id={listId} className="console-session__ledger-run" aria-label={label}>
          {row.entries.map((entry) => (
            <EntryRow key={entry.id} entry={entry} t={t} nested />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

/** The local clock time of an RFC 3339 instant, `HH:MM`. */
function formatClock(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat('en', { hour: '2-digit', minute: '2-digit', hour12: false }).format(date);
}
